import React, { useEffect, useState } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { CloseRounded, DescriptionOutlined } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import colors from '../../assets/theme/colors';
import CareSourceFileUploadButton from '../careSourceProcessing/CareSourceFileUploadButton';
import {
  linkPreparationDocument, MAX_PREPARATION_DOCUMENTS, unlinkPreparationDocument,
} from './customPreparationDraftService';

const CustomPreparationDocuments = ({ childId, draftId, initialDocuments = [], suggestionLabel = '' }) => {
  const [documents, setDocuments] = useState(initialDocuments);
  const [error, setError] = useState('');

  useEffect(() => {
    setDocuments(Array.isArray(initialDocuments) ? initialDocuments : []);
  }, [initialDocuments]);

  const handleSaved = async (result) => {
    setError('');
    try {
      const sourceNoteId = result?.existingSourceNoteId || result?.sourceNoteId;
      const next = await linkPreparationDocument({
        childId,
        draftId,
        sourceNoteId,
        label: result?.fileName || suggestionLabel || 'Uploaded document',
        status: result?.organizing === false ? 'ready' : 'reading',
      });
      setDocuments(next);
    } catch (uploadError) {
      setError(uploadError?.message || 'The document could not be linked.');
    }
  };

  const remove = async (sourceNoteId) => {
    setError('');
    try {
      setDocuments(await unlinkPreparationDocument({ childId, draftId, sourceNoteId }));
    } catch (removeError) {
      setError(removeError?.message || 'The document could not be removed.');
    }
  };

  if (!draftId) return null;
  return (
    <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: '20px' }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <DescriptionOutlined sx={{ color: '#765CCB' }} />
        <Box>
          <Typography sx={{ fontWeight: 800 }}>Add documents for this preparation</Typography>
          <Typography sx={{ fontSize: '0.82rem', color: alpha(colors.semantic.ink, 0.62) }}>
            Optional. Add up to {MAX_PREPARATION_DOCUMENTS}; Lifelog will read them in the background.
          </Typography>
        </Box>
      </Stack>
      {documents.length ? (
        <Stack spacing={0.7} sx={{ my: 1.2 }}>
          {documents.map((item) => (
            <Box key={item.sourceNoteId} sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'space-between', p: 1, borderRadius: '12px', bgcolor: alpha('#765CCB', 0.045) }}>
              <Box>
                <Typography sx={{ fontWeight: 700 }}>{item.label || 'Uploaded document'}</Typography>
                <Typography sx={{ fontSize: '0.76rem', color: alpha(colors.semantic.ink, 0.58) }}>
                  {item.status === 'ready' ? 'Ready' : item.status === 'needs_attention' ? 'Needs attention' : 'Reading'}
                </Typography>
              </Box>
              <Button size="small" startIcon={<CloseRounded />} onClick={() => remove(item.sourceNoteId)}>Remove</Button>
            </Box>
          ))}
        </Stack>
      ) : null}
      <CareSourceFileUploadButton
        childId={childId}
        sourceContext="document"
        sourceType="file_import"
        label="Upload a document"
        variantStyle="document"
        disabled={documents.length >= MAX_PREPARATION_DOCUMENTS}
        disabledText={`You can add up to ${MAX_PREPARATION_DOCUMENTS} documents.`}
        onSaved={handleSaved}
        onError={(nextError) => setError(nextError?.message || 'The document could not be uploaded.')}
      />
      {error ? <Typography role="alert" sx={{ mt: 0.8, color: '#8F3333', fontSize: '0.8rem' }}>{error}</Typography> : null}
    </Paper>
  );
};

export default CustomPreparationDocuments;
